// Xero DTOs — BUILD_SPEC.md §5.9 (xero_connections/xero_mappings/xero_sync_log),
// §12 (Xero sync v1: PENDING-until-configured connection, ACCOUNT/TAX_RATE
// mappings). Owned by G11 (plan.md) — this is the ONLY core file G11 fills; the
// barrel (schemas/index.ts) already re-exports it.
//
// Status/kind/direction enums are deliberately NOT sourced from
// `@zebra/core/enums.ts`: this goal's S0 registry rule is "touch ONLY
// packages/db/src/schema/xero.ts (+rls), packages/core/src/schemas/xero.ts,
// services/api/src/modules/xero/, colocated tests" — enums.ts is G2's foundation
// file and out of scope here (same precedent as packages/core/src/schemas/stores.ts).
import { z } from 'zod';
import { ulidSchema } from './common.js';
import { INVOICE_STATUSES } from '../enums.js';
export const XERO_CONNECTION_STATUSES = ['PENDING', 'CONNECTED', 'ERROR'];
export const XERO_MAPPING_KINDS = ['ACCOUNT', 'TAX_RATE'];
export const XERO_SYNC_DIRECTIONS = ['PUSH', 'PULL'];
export const XERO_SYNC_STATUSES = ['PENDING', 'SUCCESS', 'ERROR'];
// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------
/** `GET /xero/connect` response. When `configured` is false (no XERO_CLIENT_ID/
 * SECRET/REDIRECT_URI in the environment) there is no `authorizationUrl` — the
 * system stays fully functional in PENDING state (BUILD_SPEC.md §12) and `message`
 * explains why, rather than the request failing. */
export const xeroConnectResponseSchema = z.object({
    configured: z.boolean(),
    authorizationUrl: z.string().optional(),
    message: z.string().optional(),
});
/** `GET /xero/callback` query string. `error`/`error_description` are what Xero
 * sends instead of `code` if the user declines consent. */
export const xeroCallbackQuerySchema = z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
});
export const xeroConnectionResponseSchema = z.object({
    tenantId: ulidSchema,
    xeroTenantId: z.string().nullable(),
    status: z.enum(XERO_CONNECTION_STATUSES),
    connectedAt: z.coerce.date().nullable(),
    lastError: z.string().nullable(),
    /** Runtime flag (not persisted) — whether XERO_CLIENT_ID/SECRET/REDIRECT_URI are
     * currently present in the environment. Lets the UI explain a stuck-PENDING
     * connection without the client needing to know the env var names. */
    configured: z.boolean(),
});
// ---------------------------------------------------------------------------
// Mappings
// ---------------------------------------------------------------------------
export const xeroMappingCreateSchema = z.object({
    kind: z.enum(XERO_MAPPING_KINDS),
    key: z.string().min(1).max(100),
    xeroValue: z.string().min(1).max(200),
});
/** `kind`/`key` identify a mapping row and are immutable after creation (delete +
 * recreate to change them); only the Xero-side value is ever updated in place. */
export const xeroMappingUpdateSchema = z.object({
    xeroValue: z.string().min(1).max(200),
});
export const xeroMappingResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    kind: z.enum(XERO_MAPPING_KINDS),
    key: z.string(),
    xeroValue: z.string(),
});
/** `GET /xero/mappings/validate` response. Two modes (G-D, BUILD_SPEC.md §12
 * "Account/tax mappings validated at connect time"): `mode: 'live'` when the
 * tenant's Xero connection is CONNECTED — every mapping's `xeroValue` is checked
 * against the connected org's real chart of accounts/tax rates, surfaced in
 * `invalidAccountKeys`/`invalidTaxRateKeys`; `mode: 'presence-only'` (the
 * original G11 stub behaviour, still the fallback for a PENDING/ERROR
 * connection — the system stays fully functional before Xero credentials
 * exist) only checks that a mapping row exists at all for each required key,
 * via `missingAccountKeys`/`missingTaxRateKeys`. */
export const XERO_MAPPING_VALIDATION_MODES = ['live', 'presence-only'];
export const xeroMappingValidationResponseSchema = z.object({
    valid: z.boolean(),
    missingAccountKeys: z.array(z.string()),
    missingTaxRateKeys: z.array(z.string()),
    /** Present mapping rows whose `xeroValue` does not match any real account code
     * in the connected org. Always `[]` in `presence-only` mode. */
    invalidAccountKeys: z.array(z.string()),
    /** Same as `invalidAccountKeys`, for TAX_RATE mappings vs the org's tax types. */
    invalidTaxRateKeys: z.array(z.string()),
    mode: z.enum(XERO_MAPPING_VALIDATION_MODES),
});
// ---------------------------------------------------------------------------
// Push + pull (goal G17, §12) — extends this file only; the tables above are
// unchanged (xero_sync_log already had every column this needs).
// ---------------------------------------------------------------------------
/** What kind of our-side document a push/pull row concerns. `GOODS_RECEIPT` and
 * `LANDED_COST_DOC` push ACCPAY bills; `INVOICE` pushes ACCREC; `CREDIT_NOTE` pushes
 * a Xero credit note. Stored as free text in `xero_sync_log.entity_type` (no DB enum
 * — see schema/xero.ts header), mirrored here for response typing only. */
export const XERO_PUSH_ENTITY_TYPES = ['INVOICE', 'CREDIT_NOTE', 'GOODS_RECEIPT', 'LANDED_COST_DOC'];
/** `ALREADY_SYNCED` is a true no-op (no Xero call made, nothing logged) — see
 * services/api/src/modules/xero/push.ts's idempotency check ("before create, check
 * our record has no xero_invoice_id"). `SYNCED`/`ERROR` always correspond to at
 * least one freshly-logged `xero_sync_log` row. */
export const XERO_PUSH_OUTCOMES = ['SYNCED', 'ALREADY_SYNCED', 'ERROR'];
export const xeroPushResultSchema = z.object({
    entityType: z.enum(XERO_PUSH_ENTITY_TYPES),
    entityId: ulidSchema,
    outcome: z.enum(XERO_PUSH_OUTCOMES),
    xeroId: z.string().nullable(),
    error: z.string().nullable(),
});
/** `POST /xero/push` response. `enabled: false` (PENDING/ERROR connection) is a
 * clear no-op, never a crash — BUILD_SPEC.md §12's "system fully functional before
 * credentials exist". */
export const xeroPushSummaryResponseSchema = z.object({
    enabled: z.boolean(),
    results: z.array(xeroPushResultSchema),
    message: z.string().optional(),
});
/** `POST /xero/push/invoices/:id` response — single-entity twin of the summary above. */
export const xeroInvoicePushResponseSchema = z.object({
    enabled: z.boolean(),
    result: xeroPushResultSchema.nullable(),
    message: z.string().optional(),
});
export const xeroPaymentPullResultSchema = z.object({
    invoiceId: ulidSchema,
    xeroInvoiceId: z.string(),
    previousStatus: z.enum(INVOICE_STATUSES),
    newStatus: z.enum(INVOICE_STATUSES),
    paid: z.boolean(),
});
/** `POST /xero/sync/payments` response. */
export const xeroPaymentsPullResponseSchema = z.object({
    enabled: z.boolean(),
    results: z.array(xeroPaymentPullResultSchema),
    message: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Sync log (read) — `GET /xero/sync-log` (goal G5). `xero_sync_log` already
// records every push/pull attempt (populated by push.ts/pull.ts/retry.ts, goal
// G17) but had no read endpoint or UI; this is a read-only, cursor-paginated
// view over it, so no new columns and no new writes. `entityType` is a plain
// string filter (not restricted to `XERO_PUSH_ENTITY_TYPES`) because PULL rows
// may log entity types outside that push-only list, and the DB column itself is
// free text (see schema/xero.ts's header on why there is no DB enum for it).
// ---------------------------------------------------------------------------
export const xeroSyncLogListQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    status: z.enum(XERO_SYNC_STATUSES).optional(),
    entityType: z.string().optional(),
});
export const xeroSyncLogResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    entityType: z.string(),
    entityId: z.string(),
    direction: z.enum(XERO_SYNC_DIRECTIONS),
    status: z.enum(XERO_SYNC_STATUSES),
    attempt: z.number().int(),
    error: z.string().nullable(),
    xeroId: z.string().nullable(),
    syncedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
});
//# sourceMappingURL=xero.js.map